/**
 * AdminUserCheckinSanity — per-user 7-day data-sanity grid.
 *
 * Renders a green/grey dot grid showing exactly which sections each beta
 * tester populated in their last 7 daily check-ins. Designed to catch
 * silent-drop regressions (like the SmartCheckIn `extra="ignore"` bug we
 * hit yesterday) in seconds.
 *
 * URL: /admin/users/:userId/sanity
 *
 * Linked from /admin/users — each row has a "Sanity" button.
 */
/* eslint-disable react-hooks/exhaustive-deps */

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, CheckCircle2, Circle, RefreshCw, AlertTriangle, ShieldAlert } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  } catch { return iso; }
};

const SECTION_LABELS = {
  feelings:       'Feelings',
  sleep:          'Sleep (basic)',
  sleep_quality:  'Sleep quality',
  hydration:      'Hydration',
  food:           'Food',
  activity:       'Activity',
  caffeine:       'Caffeine',
  sugary_drinks:  'Sugary drinks',
  medication:     'Medication',
  emotions:       'Emotions',
  stress:         'Stress',
  symptoms:       'Symptoms',
  lifestyle:      'Lifestyle habits',
  mood:           'Mood',
  energy:         'Energy',
  environment:    'Environment',
};

const AdminUserCheckinSanity = () => {
  const navigate = useNavigate();
  const { userId } = useParams();
  const { user } = useAuth();
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    if (user && user !== null && user.role !== 'admin') navigate('/dashboard');
  }, [user, navigate]);

  const load = useCallback(async () => {
    setState({ loading: true, data: null, error: null });
    try {
      const { data } = await axios.get(`${API}/admin/users/${userId}/checkin-sanity`, { withCredentials: true });
      setState({ loading: false, data, error: null });
    } catch (e) {
      const s = e?.response?.status;
      const msg = s === 404 ? 'User not found.' : s === 403 ? 'Admin access required.' : 'Could not load sanity data.';
      setState({ loading: false, data: null, error: msg });
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const { loading, data, error } = state;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-gray-100 sticky top-0 md:top-16 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-3.5 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/users')} data-testid="sanity-back">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="min-w-0 flex-1">
            <h1 className="text-lg sm:text-xl font-bold text-gray-900 leading-tight">
              Check-in Data Sanity
            </h1>
            <p className="text-[11px] text-gray-500 truncate">
              {data ? `${data.email} · last ${data.window_days} days` : 'Loading…'}
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="sanity-refresh">
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 pb-24">
        {error && (
          <div className="rounded-xl border border-rose-100 bg-rose-50 text-rose-700 text-sm p-3 mb-4 flex items-center gap-2" data-testid="sanity-error">
            <ShieldAlert className="h-4 w-4" />
            {error}
          </div>
        )}

        {data && (
          <>
            {/* Snapshot */}
            <Card className="border border-gray-100 rounded-2xl shadow-sm mb-4">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Snapshot</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <dl className="grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-1.5 text-xs">
                  <div><dt className="text-gray-500 inline">Name:</dt> <dd className="inline font-medium text-gray-900">{data.name || '—'}</dd></div>
                  <div><dt className="text-gray-500 inline">Email:</dt> <dd className="inline text-gray-800">{data.email}</dd></div>
                  <div><dt className="text-gray-500 inline">Timezone:</dt> <dd className="inline text-gray-800">{data.timezone || '—'}</dd></div>
                  <div><dt className="text-gray-500 inline">Email verified:</dt> <dd className="inline text-gray-800">{data.email_verified ? 'yes' : 'no'}</dd></div>
                  <div><dt className="text-gray-500 inline">Signed up:</dt> <dd className="inline text-gray-800">{data.signed_up_at ? new Date(data.signed_up_at).toLocaleString() : '—'}</dd></div>
                  <div><dt className="text-gray-500 inline">Days logged ({data.window_days}d window):</dt> <dd className="inline text-gray-800 font-semibold">{data.total_days_logged}</dd></div>
                </dl>
              </CardContent>
            </Card>

            {/* Empty state */}
            {data.total_days_logged === 0 ? (
              <Card className="border border-amber-200 bg-amber-50/40 rounded-2xl shadow-sm" data-testid="sanity-empty">
                <CardHeader>
                  <CardTitle className="text-sm flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-amber-600" />
                    No check-ins in the last {data.window_days} days
                  </CardTitle>
                  <CardDescription className="text-xs">
                    The user has not submitted any daily check-in within the trailing 7-day window.
                  </CardDescription>
                </CardHeader>
              </Card>
            ) : (
              <>
                {/* Per-day grid */}
                <Card className="border border-gray-100 rounded-2xl shadow-sm mb-4 overflow-hidden" data-testid="sanity-grid">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Per-day populated sections</CardTitle>
                    <CardDescription className="text-xs">
                      Green dot = the user populated at least one field in that section that day. Grey = empty.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="overflow-x-auto">
                      <table className="w-full text-[11px]">
                        <thead>
                          <tr className="text-gray-500 text-left">
                            <th className="font-medium py-2 pr-3 sticky left-0 bg-white">Section</th>
                            {data.days.map((d) => (
                              <th key={d.date} className="font-medium py-2 px-1.5 text-center whitespace-nowrap">{fmtDate(d.date)}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {data.section_list.map((section) => (
                            <tr key={section} className="border-t border-gray-50">
                              <td className="py-1.5 pr-3 sticky left-0 bg-white text-gray-800">
                                {SECTION_LABELS[section] || section}
                              </td>
                              {data.days.map((d) => {
                                const ok = !!d.sections[section];
                                return (
                                  <td key={d.date} className="py-1.5 px-1.5 text-center" data-testid={`sanity-cell-${section}-${d.date}`}>
                                    {ok
                                      ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 inline-block" aria-label="populated" />
                                      : <Circle className="h-3.5 w-3.5 text-gray-200 inline-block" aria-label="empty" />}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                          <tr className="border-t-2 border-gray-200 bg-slate-50/50">
                            <td className="py-2 pr-3 sticky left-0 bg-slate-50/50 font-semibold text-gray-900">Day total</td>
                            {data.days.map((d) => (
                              <td key={d.date} className="py-2 px-1.5 text-center text-gray-900 font-semibold">
                                {d.populated_count}/{d.total_sections}
                              </td>
                            ))}
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>

                {/* Coverage summary */}
                <Card className="border border-gray-100 rounded-2xl shadow-sm">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Section coverage in window</CardTitle>
                    <CardDescription className="text-xs">
                      How many of the last {data.total_days_logged} logged days touched each section.
                      A section consistently at 0 across all testers is a signal that the field is
                      being silently dropped at the API or UI boundary.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2" data-testid="sanity-coverage">
                      {data.section_list.map((section) => {
                        const n = data.coverage_summary[section] || 0;
                        const pct = data.total_days_logged > 0 ? Math.round((n / data.total_days_logged) * 100) : 0;
                        const tone = pct === 0 ? 'bg-rose-50 border-rose-100 text-rose-700' :
                                     pct < 40 ? 'bg-amber-50 border-amber-100 text-amber-800' :
                                                'bg-emerald-50 border-emerald-100 text-emerald-800';
                        return (
                          <div key={section} className={`rounded-lg border px-2.5 py-2 ${tone}`} data-testid={`sanity-coverage-${section}`}>
                            <p className="text-[11px] font-medium">{SECTION_LABELS[section] || section}</p>
                            <p className="text-xs mt-0.5">{n}/{data.total_days_logged} days · {pct}%</p>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminUserCheckinSanity;
